import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, RefreshCw, Loader2, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-none bg-[#F7F5F0] border border-black/15 px-5 py-4 text-base text-[#1C1B1A] placeholder:text-[#1C1B1A]/40 outline-none focus:border-[#E30613] focus:bg-white transition-[border-color,background-color] duration-300";

function formatApiError(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || "").join(" ");
  return "Errore imprevisto";
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("fkl_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState(null);

  const fetchQuotes = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${t}` } });
      setQuotes(data.quotes);
    } catch {
      localStorage.removeItem("fkl_token");
      setToken(null);
      toast.error("Sessione scaduta. Accedi di nuovo.");
    }
  }, []);

  useEffect(() => {
    if (token) fetchQuotes(token);
  }, [token, fetchQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("fkl_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("fkl_token");
    setToken(null);
    setQuotes(null);
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-[#F4F1EA] text-[#1C1B1A] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Link data-testid="admin-back-home-link" to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#1C1B1A]/50 hover:text-[#E30613] transition-colors duration-300 mb-10">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
          <form data-testid="admin-login-form" onSubmit={login} className="border border-black/10 bg-white p-10 flex flex-col gap-6">
            <div className="flex flex-col items-start gap-4">
              <img src="/fkl-logo.png" alt="FKL srls" className="h-14 w-auto" />
              <p className="text-sm text-[#1C1B1A]/60">Area riservata — accedi per consultare le richieste di preventivo.</p>
            </div>
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
            {error && <div data-testid="admin-login-error" className="text-sm font-semibold text-[#E30613] border border-[#E30613]/30 bg-[#E30613]/5 px-4 py-3">{error}</div>}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-3 rounded-full bg-[#E30613] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-[transform,background-color] duration-300 hover:bg-[#B3050F] active:scale-95 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Accedi
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#F4F1EA] text-[#1C1B1A]">
      <header className="border-b border-black/10 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/fkl-logo.png" alt="FKL srls" className="h-10 w-auto" />
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#1C1B1A]/50">Richieste preventivo</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => fetchQuotes(token)}
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1C1B1A] hover:border-[#E30613] hover:text-[#E30613] transition-colors duration-300"
            >
              <RefreshCw className="w-4 h-4" /> Aggiorna
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1C1B1A] hover:border-[#E30613] hover:text-[#E30613] transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {quotes === null ? (
          <div className="flex items-center gap-3 text-[#1C1B1A]/60" data-testid="admin-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento...
          </div>
        ) : quotes.length === 0 ? (
          <div data-testid="admin-empty-state" className="border border-black/10 bg-white p-16 flex flex-col items-center gap-4 text-center">
            <Inbox className="w-10 h-10 text-[#1C1B1A]/25" strokeWidth={1.5} />
            <p className="text-[#1C1B1A]/60">Nessuna richiesta di preventivo per ora.</p>
          </div>
        ) : (
          <>
            <div data-testid="admin-quotes-count" className="mb-6 text-sm text-[#1C1B1A]/60">
              <span className="font-display text-4xl font-semibold text-[#1C1B1A] mr-2">{quotes.length}</span>
              richieste totali
            </div>
            <div className="border border-black/10 bg-white overflow-x-auto">
              <table data-testid="admin-quotes-table" className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-[10px] uppercase tracking-[0.2em] text-[#1C1B1A]/45">
                    <th className="px-6 py-4 font-bold">Data</th>
                    <th className="px-6 py-4 font-bold">Nome</th>
                    <th className="px-6 py-4 font-bold">Contatti</th>
                    <th className="px-6 py-4 font-bold">Messaggio</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-black/5 hover:bg-[#F4F1EA] transition-colors duration-200 align-top">
                      <td className="px-6 py-5 whitespace-nowrap text-[#1C1B1A]/60">{new Date(q.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</td>
                      <td className="px-6 py-5 font-semibold text-[#1C1B1A]">{q.name}</td>
                      <td className="px-6 py-5 text-[#1C1B1A]/80">
                        <div>{q.email}</div>
                        <div className="text-[#1C1B1A]/50">{q.phone}</div>
                      </td>
                      <td className="px-6 py-5 text-[#1C1B1A]/80 max-w-md">{q.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
